/**
 * Drive WebDAV status / credentials / charts side page.
 */

import { h } from 'preact'
import { useCallback, useEffect, useState } from 'preact/hooks'
import { StatsChartsPanel } from './StatsChartsPanel.jsx'
import { SidePageLoader } from './SidePageLoader.jsx'
import {
  applyQuery,
  createDriveI18n,
  fetchJson,
  formatBytes,
  formatDate,
  jsonUrl,
  postJson
} from './side-utils.js'
import {
  webdav_credentials_drive_index_path,
  webdav_drive_index_path
} from '../../routes'
import { DriveSideChrome } from './DriveSideChrome.jsx'

export function WebdavPage({ props: pageProps = {} }) {
  const i18n = createDriveI18n()
  const t = (key, opts) => i18n.t(key, opts)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [tokenName, setTokenName] = useState('Synology')
  const [plaintext, setPlaintext] = useState(pageProps.plaintextToken || null)

  const load = useCallback(async (search = window.location.search) => {
    setError(null)
    setLoading(true)
    try {
      setData(await fetchJson(jsonUrl(search)))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  function changeFilters({ period, credentialId }) {
    const url = applyQuery({
      period: period ?? data?.period_days,
      credential_id: credentialId === undefined ? data?.credential_filter_id : credentialId
    })
    history.pushState({}, '', url)
    load(url.search)
  }

  async function createCredential(e) {
    e.preventDefault()
    setBusy(true)
    setPlaintext(null)
    try {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content
      const res = await fetch(webdav_credentials_drive_index_path({ format: 'json' }), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded',
          'X-CSRF-Token': csrf
        },
        credentials: 'same-origin',
        body: new URLSearchParams({ name: tokenName || 'Synology' })
      })
      const payload = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(payload.error || `HTTP ${res.status}`)
      setPlaintext(payload.plaintext_token || null)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  async function revokeCredential(cred) {
    if (!window.confirm(t('drive.webdav.revoke_confirm'))) return
    setBusy(true)
    try {
      await postJson(cred.revoke_url)
      await load()
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
    }
  }

  if (error && !data) {
    return (
      <DriveSideChrome titleKey="drive.webdav.nav" titleIcon="bi-hdd-network">
        <div class="alert alert-danger">{error}</div>
      </DriveSideChrome>
    )
  }
  if (!data || loading) {
    return (
      <DriveSideChrome titleKey="drive.webdav.nav" titleIcon="bi-hdd-network">
        <SidePageLoader label={t('drive.loading')} />
      </DriveSideChrome>
    )
  }

  const stats = data.stats || {}
  const credentials = data.credentials || []
  const events = data.events || []
  const periods = data.periods || ['7', '30', '90']

  return (
    <DriveSideChrome titleKey="drive.webdav.nav" titleIcon="bi-hdd-network">
    <div class="drive-container drive-webdav">
      {error && <div class="alert alert-warning">{error}</div>}
      {!data.enabled && (
        <div class="alert alert-warning">{t('drive.webdav.feature_disabled')}</div>
      )}

      {plaintext && (
        <div class="alert alert-success">
          <strong>{t('drive.webdav.plaintext_once')}</strong>
          <code class="user-select-all d-block mt-2 p-2 bg-light">{plaintext}</code>
          <small class="text-muted">{t('drive.webdav.plaintext_warning')}</small>
        </div>
      )}

      {data.can_manage_credentials && (
        <div class="card mb-4">
          <div class="card-header">{t('drive.webdav.connection')}</div>
          <div class="card-body">
            <dl class="row mb-3">
              <dt class="col-sm-3">{t('drive.webdav.server_url')}</dt>
              <dd class="col-sm-9">
                <code class="webdav-breakable">{data.webdav_url}</code>
              </dd>
              <dt class="col-sm-3">{t('drive.webdav.username')}</dt>
              <dd class="col-sm-9">
                <code class="webdav-breakable">{data.webdav_username}</code>
              </dd>
            </dl>
            <form class="row g-2 align-items-end" onSubmit={createCredential}>
              <div class="col-12 col-md-6">
                <input
                  type="text"
                  class="form-control"
                  placeholder="Synology"
                  value={tokenName}
                  onInput={(e) => setTokenName(e.target.value)}
                />
              </div>
              <div class="col-12 col-md-auto">
                <button type="submit" class="btn btn-primary w-100" disabled={busy}>
                  {t('drive.webdav.generate')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div class="card mb-4">
        <div class="card-body">
          <div class="row align-items-end g-2">
            <div class="col-12 col-md-3">
              <label class="form-label">{t('drive.webdav.period_filter')}</label>
              <select
                class="form-select"
                value={data.period_days}
                onChange={(e) => changeFilters({ period: e.target.value })}
              >
                {periods.map((d) => (
                  <option key={d} value={d}>
                    {t(`drive.webdav.period_${d}`)}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-12 col-md-5">
              <label class="form-label">{t('drive.webdav.token_filter')}</label>
              <select
                class="form-select"
                value={data.credential_filter_id || ''}
                onChange={(e) =>
                  changeFilters({ credentialId: e.target.value || null })
                }
              >
                <option value="">{t('drive.webdav.all_tokens')}</option>
                {credentials.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name} ({c.token_prefix}…)
                    {!c.active ? ` [${t('drive.webdav.revoked')}]` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div class="col-12 col-md-4">
              <a href={webdav_drive_index_path()} class="btn btn-outline-secondary w-100">
                {t('drive.webdav.clear_filters')}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-4">
          <div class="card">
            <div class="card-body">
              <div class="text-muted small">{t('drive.webdav.active_credentials')}</div>
              <div class="fs-3">{data.active_count}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-body">
              <div class="text-muted small">{t('drive.webdav.revoked_credentials')}</div>
              <div class="fs-3">{data.revoked_count}</div>
            </div>
          </div>
        </div>
        <div class="col-md-4">
          <div class="card">
            <div class="card-body">
              <div class="text-muted small">
                {t('drive.webdav.uploads_period', { days: data.period_days })}
              </div>
              <div class="fs-3">{stats.upload_count}</div>
              <small class="text-muted">{formatBytes(stats.upload_bytes)}</small>
            </div>
          </div>
        </div>
      </div>

      <div class="row mb-4">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">
              {t('drive.webdav.downloads_period', { days: data.period_days })}
            </div>
            <div class="card-body">
              <div class="fs-4">{stats.download_count}</div>
              <small class="text-muted">{formatBytes(stats.download_bytes)}</small>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card">
            <div class="card-header">{t('drive.webdav.by_method')}</div>
            <div class="card-body">
              {stats.by_method && Object.keys(stats.by_method).length > 0 ? (
                <ul class="mb-0">
                  {Object.entries(stats.by_method).map(([method, count]) => (
                    <li key={method}>
                      <code>{String(method).toUpperCase()}</code>: {count}
                    </li>
                  ))}
                </ul>
              ) : (
                <span class="text-muted">{t('drive.webdav.no_events')}</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <StatsChartsPanel stats={stats} chartI18n={data.chart_i18n} />

      <div class="card mb-4">
        <div class="card-header">{t('drive.webdav.credentials_summary')}</div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table mb-0">
              <thead>
                <tr>
                  <th>{t('drive.webdav.name')}</th>
                  <th>{t('drive.webdav.prefix')}</th>
                  <th>{t('drive.webdav.status')}</th>
                  <th>{t('drive.webdav.last_used')}</th>
                  {data.can_manage_credentials && <th />}
                </tr>
              </thead>
              <tbody>
                {credentials.map((cred) => (
                  <tr key={cred.id}>
                    <td>{cred.name}</td>
                    <td>
                      <code>{cred.token_prefix}…</code>
                    </td>
                    <td>
                      {cred.active ? (
                        <span class="badge bg-success">{t('drive.webdav.active')}</span>
                      ) : (
                        <span class="badge bg-secondary">{t('drive.webdav.revoked')}</span>
                      )}
                    </td>
                    <td>{cred.last_used_at ? formatDate(cred.last_used_at) : '—'}</td>
                    {data.can_manage_credentials && (
                      <td class="text-end">
                        {cred.active && (
                          <button
                            type="button"
                            class="btn btn-sm btn-outline-warning"
                            disabled={busy}
                            onClick={() => revokeCredential(cred)}
                          >
                            {t('drive.webdav.revoke')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
                {credentials.length === 0 && (
                  <tr>
                    <td
                      colspan={data.can_manage_credentials ? 5 : 4}
                      class="text-muted text-center"
                    >
                      {t('drive.webdav.no_credentials')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        {!data.can_manage_credentials && (
          <div class="card-footer text-muted small">
            {t('drive.webdav.managed_by_global_admin')}
          </div>
        )}
      </div>

      <div class="card">
        <div class="card-header">{t('drive.webdav.audit_log')}</div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-sm mb-0 webdav-audit-table">
              <thead>
                <tr>
                  <th class="webdav-audit-when">{t('drive.webdav.when')}</th>
                  <th class="webdav-audit-direction">{t('drive.webdav.direction')}</th>
                  <th class="webdav-audit-method d-none d-sm-table-cell">
                    {t('drive.webdav.method')}
                  </th>
                  <th class="webdav-audit-path-col">{t('drive.webdav.path')}</th>
                  <th class="webdav-audit-size">{t('drive.webdav.size')}</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event, idx) => (
                  <tr key={idx}>
                    <td class="webdav-audit-when text-nowrap">{formatDate(event.created_at)}</td>
                    <td class="webdav-audit-direction">
                      {event.direction
                        ? t(`drive.webdav.directions.${event.direction}`)
                        : '—'}
                    </td>
                    <td class="webdav-audit-method d-none d-sm-table-cell">
                      <code>{String(event.method || '').toUpperCase()}</code>
                    </td>
                    <td class="webdav-audit-path">{event.path}</td>
                    <td class="webdav-audit-size text-nowrap">
                      {event.byte_size != null ? formatBytes(event.byte_size) : '—'}
                    </td>
                  </tr>
                ))}
                {events.length === 0 && (
                  <tr>
                    <td colspan="5" class="text-muted text-center">
                      {t('drive.webdav.no_events')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    </DriveSideChrome>
  )
}
